import { z } from "zod";

const decideBodySchema = z.object({
	verdict: z.enum(["approve", "reject"]),
	note: z.string().max(500).optional(),
});

const decideResponseSchema = z.object({
	ok: z.boolean(),
	approval: z
		.object({
			id: z.string(),
			state: z.string(),
		})
		.nullable()
		.optional(),
	error: z.string().optional(),
	reason: z.string().optional(),
});

export type ApprovalVerdict = z.infer<typeof decideBodySchema>["verdict"];

/**
 * Same gate as Desk Approvals — first writer wins across Matrix / Slack / email.
 */
export async function decideApproval(args: {
	approvalId: string;
	verdict: ApprovalVerdict;
	note?: string;
}): Promise<
	| { ok: true; state: string }
	| { ok: false; error: string }
> {
	const response = await fetch(`/api/approvals/${args.approvalId}/decide`, {
		method: "POST",
		credentials: "include",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({
			verdict: args.verdict,
			...(args.note ? { note: args.note } : {}),
		}),
	});

	const json: unknown = await response.json().catch(() => null);
	const parsed = decideResponseSchema.safeParse(json);
	if (!response.ok || !parsed.success) {
		const err =
			parsed.success && parsed.data.error
				? parsed.data.reason || parsed.data.error
				: `Could not ${args.verdict} (${response.status})`;
		return { ok: false, error: err };
	}
	if (!parsed.data.ok || !parsed.data.approval) {
		return {
			ok: false,
			error:
				parsed.data.reason ||
				parsed.data.error ||
				"This approval is no longer open.",
		};
	}
	return { ok: true, state: parsed.data.approval.state };
}
